/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @description
 *
 * @created at Thu Jun 29 2023
 **/

import { timer, Subscription } from "rxjs";

function isIdenticalDevice(newDevice: MediaDeviceInfo, oldDevice: MediaDeviceInfo) {
	if (oldDevice.deviceId) {
		return newDevice.deviceId === oldDevice.deviceId;
	}
	function toCrudeId(device: MediaDeviceInfo) {
		return `${device.kind}:${device.groupId}`;
	}

	return toCrudeId(newDevice) === toCrudeId(oldDevice);
}

class DeviceManager {
	private _devices: MediaDeviceInfoList = [];
	private _currentDevices: MediaDeviceInfo[] = [];
	private _changedDevices: Device.DeviceChange[] = [];
	private _eventFireTimer: Subscription | null = null;
	private _eventCallback: ((device: Device.ChangeDevicesEventData) => void) | null = null;

	private static _instance: DeviceManager | null = null;
	public static get instance() {
		if (!DeviceManager._instance) {
			DeviceManager._instance = new DeviceManager();
		}
		return DeviceManager._instance;
	}

	private constructor() {
		this.resetDevices(false).then(() => {
			// console.debug(
			// 	"#DeviceManager#",
			// 	"DeviceManager is created",
			// 	this.videoInput,
			// 	this.audioInput,
			// 	this.audioOutput
			// );
		});
	}

	public set eventCallback(callback: (device: Device.ChangeDevicesEventData) => void) {
		this._eventCallback = callback;
	}

	public get eventCallback() {
		if (!this._eventCallback) {
			throw Error("eventCallback is not set");
		}
		return this._eventCallback;
	}

	/**
	 * Gets the list of media devices.
	 * @return {MediaDeviceInfoList} A list of available media devices.
	 */
	public get devices(): MediaDeviceInfoList {
		return this._devices;
	}

	/**
	 * Retrieves a list of available video input devices.
	 *
	 * Filters and returns devices of type 'videoinput' from the available media devices.
	 *
	 * @return {MediaDeviceInfo[]} An array of MediaDeviceInfo objects representing video input devices.
	 */
	public get videoInput(): MediaDeviceInfo[] {
		return this.devices.filter((item) => item.kind === "videoinput") || [];
	}

	/**
	 * Retrieves an array of audio input devices available on the system.
	 *
	 * @return {MediaDeviceInfo[]} An array of MediaDeviceInfo objects representing audio input devices. If no devices are available, an empty array is returned.
	 */
	public get audioInput(): MediaDeviceInfo[] {
		return this.devices.filter((item) => item.kind === "audioinput") || [];
	}

	/**
	 * Retrieves a list of audio output devices available.
	 *
	 * @return {MediaDeviceInfo[]} An array of MediaDeviceInfo objects representing the available audio output devices.
	 */
	public get audioOutput(): MediaDeviceInfo[] {
		return this.devices.filter((item) => item.kind === "audiooutput");
	}

	/**
	 * Retrieves the media device with the specified kind and device ID.
	 * If no device matches the given ID, it retrieves the first device of the specified kind.
	 *
	 * @param {('videoinput' | 'audioinput' | 'audiooutput')} kind - The type of media device to search for.
	 * @param {string} device_id - The unique identifier of the media device to find.
	 * @return {Device.MediaDeviceSetting} The media device details, including its ID and label.
	 */
	public get_device_with_id(
		kind: "videoinput" | "audioinput" | "audiooutput",
		device_id: string
	): Device.MediaDeviceSetting {
		let result =
			device_id.length > 0
				? this.devices.filter((item) => item.kind === kind && item.deviceId === device_id)
				: [];
		if (result.length <= 0) {
			result = this.devices.filter((item) => item.kind === kind);
		}
		return result.length
			? {
					id: result[0].deviceId,
					name: result[0].label
				}
			: {};
	}

	/**
	 * Resets the device states by enumerating the current devices and checking for changes.
	 *
	 * @param {boolean} [event_fire=false] - Flag indicating whether an event should be fired if changes are detected.
	 * @return {Promise<void>} - A promise that resolves when the devices have been reset and changes have been processed.
	 */
	public async resetDevices(event_fire: boolean = false): Promise<void> {
		const devices = await this.enumerateDevices();
		this.checkDeviceChanges(devices, event_fire);
	}

	private async enumerateDevices() {
		const devices = await navigator.mediaDevices.enumerateDevices();
		return devices.filter(
			(device) => device.deviceId !== "default" && device.deviceId !== "communications" && device.deviceId !== ""
		);
	}

	private checkDeviceChanges(newDevices: MediaDeviceInfo[], eventFire = true) {
		const oldDevices = this._devices;
		this._devices = newDevices;

		if (eventFire) {
			const changedDevices = this.calculateDeviceChanges(newDevices, oldDevices);
			if (changedDevices.length) {
				this._changedDevices = this._changedDevices.concat(changedDevices);
				this._currentDevices = newDevices.map(this.normalizeDeviceInfo);
				if (this._eventFireTimer === null) {
					this._eventFireTimer = timer(800).subscribe(() => {
						this._eventCallback?.({
							changedDevices: this._changedDevices,
							devices: this._currentDevices
						});

						this._changedDevices = [];
						this._currentDevices = [];
						this._eventFireTimer = null;
					});
				}
			}
		}
	}

	private normalizeDeviceInfo(device: MediaDeviceInfo | InputDeviceInfo) {
		return {
			deviceId: device.deviceId || "",
			groupId: device.groupId || "",
			kind: device.kind,
			label: device.label || ""
		} as MediaDeviceInfo;
	}

	private calculateDeviceChanges(newDevices: MediaDeviceInfoList, oldDevices: MediaDeviceInfoList) {
		const removals = oldDevices.slice();
		const updates: Device.DeviceChange[] = [];

		const additions = newDevices.filter((newDevice) => {
			const oldDeviceIndex = removals.findIndex((oldDevice) => isIdenticalDevice(newDevice, oldDevice));
			if (oldDeviceIndex > -1) {
				const [oldDevice] = removals.splice(oldDeviceIndex, 1);

				if (newDevice.label !== oldDevice.label) {
					const update: Device.DeviceUpdateEvent = {
						type: "update",
						newInfo: newDevice,
						oldInfo: oldDevice
					};

					updates.push(update);
				}
			}

			return oldDeviceIndex === -1;
		});
		return [
			...updates,

			// A device was just removed.
			...removals.map((device) => {
				return {
					type: "remove",
					device
				} as Device.DeviceRemoveEvent;
			}),

			// A device was just plugged in.
			...additions.map((device) => {
				return { type: "add", device } as Device.DeviceAddEvent;
			})
		];
	}
}
export default DeviceManager.instance;
